const SCRIPT_REGEX = /<script([^>]*)\/*>/gi;
// @ts-expect-error - TS1503 Typescript 5.9.2 upgrade
const QUOTES_REGEX = /^(["']*)(?<value>.*?)\1$/;
const SPACE_REGEX = /\s+/;

export function parseScriptTags(
	tags: string | string[],
	linksMode = false,
	matcher = SCRIPT_REGEX,
): { [key: string]: string }[] {
	// Parse <script key="value">/script> into { key: "value" }
	if (linksMode) {
		return Array.isArray(tags) ? tags.map((t) => ({ src: t })) : [];
	}

	if (typeof tags !== 'string') return [];

	const result: { [key: string]: string }[] = [];
	for (const [, attributes] of tags.matchAll(matcher)) {
		result.push(
			Object.fromEntries(
				attributes
					.split(SPACE_REGEX)
					.filter((pair) => pair)
					.map((pair) => {
						const pos = pair.indexOf('=');
						if (pos !== -1) {
							const key = pair.substring(0, pos);
							const rawValue = pair.substring(pos + 1);
							const match = rawValue.match(QUOTES_REGEX);
							const value = match?.groups?.value ?? rawValue;
							return [key, value];
						} else {
							return [pair, pair];
						}
					}),
			),
		);
	}
	return result;
}

export function objToAttrs(attributes: { [key: string]: string }): string {
	// Convert { key: value } to key="value"
	const result = Object.entries(attributes)
		.map(([key, value]) => (value && key !== value ? `${key}="${value}"` : key))
		.join(' ');
	return result ? ` ${result}` : '';
}

export function removeJquery(tags: string): string {
	// Jquery is always loaded in packages/confluence-frontend-server/templates/html/dashboard-spa-container.js
	// So we need to remove it from the superbatch tags
	return parseScriptTags(tags)
		.filter((attrs) => attrs['data-wrm-key'] !== 'com.atlassian.plugins.jquery:jquery')
		.map((attrs) => `<script${objToAttrs(attrs)}></script>`)
		.join('\n');
}

export function deferScript(tags: string): string {
	return parseScriptTags(tags)
		.map((attributes: { [key: string]: string }) => {
			attributes['defer'] = 'defer';
			return `<script${objToAttrs(attributes)}></script>`;
		})
		.join('\n');
}

const srcExtractor = /src=["'](.*?)["']/gi;
export function convertScriptTagsToPreloadTags(jsTags: string): string {
	if (!jsTags) return '';
	return [...jsTags.matchAll(srcExtractor)]
		.map((match) => match[1])
		.filter((src) => !!src)
		.map((src) => `<link rel="preload" href="${src}" as="script">`)
		.join('\n');
}

export function convertSuperBatchResourceToTags(
	resource: {
		url: string;
		type: 'css' | 'js';
		attributes: Array<{ key: string; value: string }>;
		__typename: string;
	},
	asyncScript = false,
) {
	const nonce = window.__SSR_NONCE_VALUE__;
	const attributes = Array.isArray(resource?.attributes)
		? Object.fromEntries(resource.attributes.map((attr) => [attr.key, attr.value]))
		: resource.attributes || {};

	if (resource.type === 'css') {
		return `<link rel="stylesheet" nonce="${nonce}" href="${resource.url}"${objToAttrs(attributes)} />`;
	} else if (resource.type === 'js') {
		return `<script nonce="${nonce}" src="${resource.url}"${objToAttrs(attributes)}${asyncScript ? ' async fetchpriority="high"' : ''}></script>`;
	}
	return '';
}
